import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import type { CategoryResponse, EmailSettings } from "./settings-types";
import { CardSkeleton, SaveButton } from "./settings-ui";

// ============================================================
// Email Rules — Spam, Exclude Senders, Booking Keywords
// ============================================================

export function EmailRulesCard() {
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: ["settings", "email"],
		queryFn: () =>
			api.get<CategoryResponse<EmailSettings>>("/settings/category/email"),
	});

	const [spamDomains, setSpamDomains] = useState("");
	const [excludeSenders, setExcludeSenders] = useState("");
	const [bookingKeywords, setBookingKeywords] = useState("");
	const [autoCreate, setAutoCreate] = useState(true);

	useEffect(() => {
		if (query.data) {
			const d = query.data.data;
			setSpamDomains(d.spamDomains.join(", "));
			setExcludeSenders(d.excludeSenders.join(", "));
			setBookingKeywords(d.bookingKeywords.join(", "));
			setAutoCreate(d.autoCreateBooking);
		}
	}, [query.data]);

	const mutation = useMutation({
		mutationFn: (data: Partial<EmailSettings>) =>
			api.put("/settings/category/email", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["settings", "email"] });
		},
	});

	function handleSave() {
		mutation.mutate({
			spamDomains: spamDomains
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			excludeSenders: excludeSenders
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			bookingKeywords: bookingKeywords
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
			autoCreateBooking: autoCreate,
		});
	}

	if (query.isLoading) return <CardSkeleton />;

	return (
		<div className="space-y-3">
			<h3 className="font-medium text-stone-700 flex items-center gap-2">
				<Mail size={16} className="text-blue-600" />
				Schritt 2 & 3: Absender-Filter & Keywords
			</h3>
			<div className="card space-y-4">
				<div>
					<label
						htmlFor="spam-domains"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Spam-Domains (kommasepariert)
					</label>
					<input
						id="spam-domains"
						value={spamDomains}
						onChange={(e) => setSpamDomains(e.target.value)}
						className="input text-sm"
						placeholder="newsletter, noreply@linkedin, marketing@"
					/>
					<p className="text-xs text-stone-400 mt-1">
						E-Mails von diesen Domains werden ignoriert
					</p>
				</div>

				<div>
					<label
						htmlFor="exclude-senders"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Absender ausschließen (kommasepariert)
					</label>
					<input
						id="exclude-senders"
						value={excludeSenders}
						onChange={(e) => setExcludeSenders(e.target.value)}
						className="input text-sm"
						placeholder="paypal@, amazon@, noreply@"
					/>
					<p className="text-xs text-stone-400 mt-1">
						E-Mails von diesen Absendern werden komplett ignoriert
					</p>
				</div>

				<div>
					<label
						htmlFor="booking-kw"
						className="text-xs font-medium text-stone-500 block mb-1"
					>
						Buchungs-Keywords (kommasepariert)
					</label>
					<textarea
						id="booking-kw"
						value={bookingKeywords}
						onChange={(e) => setBookingKeywords(e.target.value)}
						className="input text-sm min-h-[60px]"
						rows={2}
					/>
					<p className="text-xs text-stone-400 mt-1">
						E-Mails mit 3+ Keywords werden als Buchung erkannt
					</p>
				</div>

				<label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
					<input
						type="checkbox"
						checked={autoCreate}
						onChange={(e) => setAutoCreate(e.target.checked)}
						className="rounded border-stone-300"
					/>
					Buchungen automatisch aus E-Mails erstellen
				</label>

				<SaveButton
					isPending={mutation.isPending}
					isSuccess={mutation.isSuccess}
					onClick={handleSave}
				/>
			</div>
		</div>
	);
}
